import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useCartOperationStatus } from "../../../../hooks/useCartOperationStatus";
import { calculateItemsSubtotal } from "../../../../utils/cartCalculationUtils";
import MobileCartSummary from "./MobileCartSummary";
import DesktopCartSummary from "./DesktopCartSummary";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

interface Props {
  isCheckoutDisabled: boolean;
  onProceedToCheckout: () => Promise<{
    isValid: boolean;
    updatedCartGroups?: any;
    errors?: any[];
  }>;
}

const CartSummary: React.FC<Props> = ({
  isCheckoutDisabled,
  onProceedToCheckout,
}) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 769);
  const navigate = useNavigate();
  const [validationState, setValidationState] = useState({
    isValidating: false,
    validationStep: "",
  });

  // Get cart state from Redux
  const cart = useSelector((state: any) => state.cart);
  const { groups, optimisticProducts } = cart;

  // Get location state
  const locationState = useSelector((state: any) => state.location);
  const hasLocationSet =
    !!locationState?.address && !!locationState?.deliveryFeeZone?.documentId;

  // Check for active cart operations (global operations)
  const { isLoading: isAnyOperationInProgress } = useCartOperationStatus();

  // Check if any group has active operations - NEW!
  const { isLoading: isAnyGroupUpdating } = useCartOperationStatus(
    undefined,
    undefined,
    { anyGroup: true }
  );

  // Calculate values for cart summary with optimistic subtotal
  const { subtotal, totalProducts, totalDeliveryFee, total } = useMemo(() => {
    // Calculate total number of products with optimistic quantities
    const allCartItems = [];

    if (groups) {
      for (const group of groups) {
        if (group.products) {
          allCartItems.push(...group.products);
        }
      }
    }

    // Calculate total products count
    const totalProds =
      allCartItems.length > 0
        ? allCartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
        : 0;

    // Calculate subtotal
    const calculatedSubtotal = calculateItemsSubtotal(allCartItems);

    // Calculate delivery fee from groups
    const deliveryFee =
      groups?.reduce(
        (sum: number, group: any) => sum + (group.deliveryFee || 0),
        0
      ) || 0;

    return {
      subtotal: calculatedSubtotal,
      totalProducts: totalProds,
      totalDeliveryFee: deliveryFee,
      total: calculatedSubtotal + deliveryFee,
    };
  }, [groups, optimisticProducts]);

  // Handle checkout process
  const handleCheckout = async () => {
    if (validationState.isValidating) return;

    setValidationState({
      isValidating: true,
      validationStep: "Validando disponibilidad de productos...",
    });

    try {
      const validationResult = await onProceedToCheckout();

      if (validationResult.isValid) {
        setValidationState({
          isValidating: true,
          validationStep: "Redirigiendo al checkout...",
        });

        navigate("/checkout");
      } else {
        setValidationState({
          isValidating: false,
          validationStep: "",
        });
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      setValidationState({
        isValidating: false,
        validationStep: "",
      });
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 769);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine if fee calculation is in progress
  const isFeeCalculating = useMemo(() => {
    // Check if any group has undefined or -1 delivery fee
    const hasUncalculatedDeliveryFee =
      groups?.some(
        (group: any) =>
          group.deliveryFee === undefined ||
          group.deliveryFee === null ||
          group.deliveryFee === -1
      ) || false;

    return (
      isAnyOperationInProgress ||
      isAnyGroupUpdating || // Include group updates check
      (hasLocationSet && hasUncalculatedDeliveryFee)
    );
  }, [groups, isAnyOperationInProgress, isAnyGroupUpdating, hasLocationSet]);

  // Add a new flag for total updates based on any group updates
  const isTotalCalculating = useMemo(() => {
    return isFeeCalculating || isAnyGroupUpdating;
  }, [isFeeCalculating, isAnyGroupUpdating]);

  // Check if all groups have calculated delivery fees
  const allGroupsHaveDeliveryFees = useMemo(() => {
    return (
      groups?.every((group: any) => {
        return (
          group.deliveryFee !== undefined &&
          group.deliveryFee !== null &&
          group.deliveryFee !== -1
        );
      }) || false
    );
  }, [groups]);

  // Check if any dates are missing from groups
  const hasGroupsMissingDates = useMemo(() => {
    return groups?.some((group: any) => !group.deliveryDate) || false;
  }, [groups]);

  // Prepare shared props for both mobile and desktop components
  const summaryProps = {
    isCheckoutDisabled:
      isCheckoutDisabled ||
      validationState.isValidating ||
      !hasLocationSet ||
      !allGroupsHaveDeliveryFees ||
      hasGroupsMissingDates,
    onCheckout: handleCheckout,
    total,
    subtotal,
    totalProducts,
    totalDeliveryFee,
    isUpdatingFees: isFeeCalculating,
    isUpdatingTotal: isTotalCalculating, // NEW! Pass total calculation state
    hasLocationSet,
    allGroupsHaveDeliveryFees,
    hasGroupsMissingDates,
    showStatusMessages: true,
  };

  return (
    <>
      {validationState.isValidating && (
        <LoadingOverlay message={validationState.validationStep} />
      )}
      {isDesktop ? (
        <DesktopCartSummary {...summaryProps} />
      ) : (
        <MobileCartSummary {...summaryProps} />
      )}
    </>
  );
};

export default CartSummary;
